import { computed } from '@ember/object';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import EmberDataBaseModel from '@square/dashboard-data/models/ember-data-base-model';
import { condition } from '@square/dashboard-utilities/macros';
import { HeadlineFeeRateName } from '@square/dashboard-protos/models/squareup/feeplans/pricing/headline_fee_rates';
import { notEmpty } from '@ember/object/computed';
export default EmberDataBaseModel.extend({
  // V2 seller display rates (populated when API returns seller_display_rates format)
  sellerDisplayRates: fragmentArray('seller-display-rate'),
  /**
   * Whether this merchant has headline rates (V2 format).
   * When true, use rates.* properties to access fees.
   * When false, the merchant is on V1 legacy format.
   */
  hasHeadlineRates: notEmpty('sellerDisplayRates'),
  /**
   * Whether this merchant has distinct card not present rates for keyed vs on-file entry.
   * ES merchants have separate CARD_NOT_PRESENT_KEYED and CARD_NOT_PRESENT_ON_FILE rates.
   * US merchants have a single CARD_NOT_PRESENT rate used for both.
   */
  hasDistinctCnpRates: computed('sellerDisplayRates.[]', function () {
    return Boolean(this.sellerDisplayRates?.toArray().some(rate => Number(rate.headlineRateName) === HeadlineFeeRateName.CARD_NOT_PRESENT_KEYED));
  }),
  /**
   * Convenience method to look up a specific rate by its headline name.
   *
   * @param {number} headlineRateName - A HeadlineFeeRateName enum value:
   *   - HeadlineFeeRateName.CARD_PRESENT (1) - In-person card payments
   *   - HeadlineFeeRateName.CARD_NOT_PRESENT (2) - Manual card entry
   *   - HeadlineFeeRateName.ONLINE (3) - Online payments
   *   - HeadlineFeeRateName.ACH_INVOICES (6) - ACH bank transfers
   *   - HeadlineFeeRateName.AFTERPAY (7) - Afterpay/Clearpay
   *   - HeadlineFeeRateName.CARD_PRESENT_PAID (20) - Card present with subscription
   *   See: @square/dashboard-protos/models/squareup/feeplans/pricing/headline_fee_rates
   * @returns {SellerDisplayRate|undefined} The matching rate or undefined
   *
   * @example
   * import { HeadlineFeeRateName } from '@square/dashboard-protos/models/squareup/feeplans/pricing/headline_fee_rates';
   * const cardPresentRate = processingFees.getRateByName(HeadlineFeeRateName.CARD_PRESENT);
   * if (cardPresentRate) {
   *   console.log(cardPresentRate.formatted); // "2.6% + 10¢"
   * }
   */
  getRateByName(headlineRateName) {
    return this.sellerDisplayRates?.toArray().find(rate => rate.headlineRateName === headlineRateName);
  },
  /**
   * Resolved processing rates that work for both V1 (legacy fee plans) and V2 (headline rates).
   *
   * For V2 headline rate merchants, returns the mapped headline rate.
   * For V1 legacy merchants, returns the original fragment.
   *
   * Both return types have the same interface: `formatted`, `formattedConstraints`, `equals()`.
   *
   * Note: The API returns either seller_display_rates (V2) OR processing_fees_collection (V1),
   * never both. So when hasHeadlineRates is true, V1 fragments are empty, and vice versa.
   *
   * @example
   * const rate = processingFees.rates.retailPosFree;
   * console.log(rate.formatted); // "1.25% + 5¢"
   */
  rates: computed('sellerDisplayRates.[]', 'cardNotPresent', 'cardOnFile', 'cardPresent', 'hasDistinctCnpRates', 'hasHeadlineRates', 'retailPosFree', 'retailPosPaid', function () {
    if (!this.hasHeadlineRates) {
      // V1 legacy merchant - return original fragments
      return {
        retailPosFree: this.retailPosFree,
        retailPosPaid: this.retailPosPaid,
        cardPresent: this.cardPresent,
        cardNotPresent: this.cardNotPresent,
        cardOnFile: this.cardOnFile
      };
    }
    let cardNotPresentRateName = HeadlineFeeRateName.CARD_NOT_PRESENT;
    let cardOnFileRateName = HeadlineFeeRateName.CARD_NOT_PRESENT;
    if (this.hasDistinctCnpRates) {
      cardNotPresentRateName = HeadlineFeeRateName.CARD_NOT_PRESENT_KEYED;
      cardOnFileRateName = HeadlineFeeRateName.CARD_NOT_PRESENT_ON_FILE;
    }
    return {
      retailPosFree: this.getRateByName(HeadlineFeeRateName.CARD_PRESENT),
      retailPosPaid: this.getRateByName(HeadlineFeeRateName.CARD_PRESENT_PAID),
      cardPresent: this.getRateByName(HeadlineFeeRateName.CARD_PRESENT),
      cardNotPresent: this.getRateByName(cardNotPresentRateName),
      cardOnFile: this.getRateByName(cardOnFileRateName)
    };
  }),
  // V1 fragment attributes (populated when API returns processing_fees_collection format)
  cardPresent: fragment('processing-fee'),
  cardNotPresent: fragment('processing-fee'),
  cardOnFile: fragment('processing-fee'),
  ecomSosFree: fragment('processing-fee'),
  ecomSosPremium: fragment('processing-fee'),
  nonEuEcomSosFree: fragment('processing-fee'),
  nonEuEcomSosPremium: fragment('processing-fee'),
  invoice: fragment('processing-fee'),
  invoiceCardOnFile: fragment('processing-fee'),
  invoiceDashboard: fragment('processing-fee'),
  invoicesAch: fragment('processing-fee'),
  invoicesAchPlus: fragment('processing-fee'),
  invoicesAfterpay: fragment('processing-fee'),
  invoicesCashappPay: fragment('processing-fee'),
  invoicesPayPageFree: fragment('processing-fee'),
  invoicesPayPagePlus: fragment('processing-fee'),
  externalApi: fragment('processing-fee'),
  nonEuExternalApi: fragment('processing-fee'),
  terminal: fragment('processing-fee'),
  terminalCardOnFile: fragment('processing-fee'),
  registerHardware: fragment('processing-fee'),
  registerT2: fragment('processing-fee'),
  interac: fragment('processing-fee'),
  visaMastercardCardPresent: fragment('processing-fee'),
  americanExpressCardPresent: fragment('processing-fee'),
  cafisJcb: fragment('processing-fee'),
  cafisDiscoverDiners: fragment('processing-fee'),
  cafisJcbKeyed: fragment('processing-fee'),
  cafisDiscoverDinersKeyed: fragment('processing-fee'),
  cafisJcbInvoice: fragment('processing-fee'),
  cafisDiscoverDinersInvoice: fragment('processing-fee'),
  cafisDiscoverDinersInvoiceDashboard: fragment('processing-fee'),
  cafisJcbInvoiceCardOnFile: fragment('processing-fee'),
  cafisJcbInvoiceDashboard: fragment('processing-fee'),
  cafisDiscoverDinersInvoiceCardOnFile: fragment('processing-fee'),
  cafisJcbTerminal: fragment('processing-fee'),
  cafisJcbTerminalOnFile: fragment('processing-fee'),
  cafisDiscoverDinersTerminal: fragment('processing-fee'),
  cafisDiscoverDinersTerminalOnFile: fragment('processing-fee'),
  cafisJcbExternalApi: fragment('processing-fee'),
  cafisJcbEcomFree: fragment('processing-fee'),
  cafisJcbEcomPremium: fragment('processing-fee'),
  cafisDiscoverDinersExternalApi: fragment('processing-fee'),
  cafisDiscoverDinersEcomFree: fragment('processing-fee'),
  cafisDiscoverDinersEcomPremium: fragment('processing-fee'),
  retailPosFree: fragment('processing-fee'),
  retailPosPaid: fragment('processing-fee'),
  restaurantsPosFree: fragment('processing-fee'),
  restaurantsPosPaid: fragment('processing-fee'),
  riskManagerFree: fragment('processing-fee'),
  riskManagerPaid: fragment('processing-fee'),
  appointmentsPosFree: fragment('processing-fee'),
  appointmentsPosPaid: fragment('processing-fee'),
  appointmentsObsKeyed: fragment('processing-fee'),
  appointmentsObsOnFile: fragment('processing-fee'),
  nonEuAppointmentsObsKeyed: fragment('processing-fee'),
  nonEuAppointmentsObsOnFile: fragment('processing-fee'),
  payPayPos: fragment('processing-fee'),
  rakutenPayPos: fragment('processing-fee'),
  merpayPos: fragment('processing-fee'),
  wechatPayPos: fragment('processing-fee'),
  dBaraiPos: fragment('processing-fee'),
  auPayPos: fragment('processing-fee'),
  alipayPos: fragment('processing-fee'),
  felicaIdPos: fragment('processing-fee'),
  felicaQpPos: fragment('processing-fee'),
  felicaTransportationPos: fragment('processing-fee'),
  giftCardLoad: fragment('processing-fee'),
  paymentLinks: fragment('processing-fee'),
  unionpayCardPresent: fragment('processing-fee'),
  internationalTransaction: fragment('processing-fee'),
  // TODO(kh) replace this when web sends this info
  // pickup: fragment('processing-fee'),

  sameRate: computed('cardPresent', 'cardNotPresent', function () {
    return this.cardPresent.equals(this.cardNotPresent);
  }),
  // Dictates whether Cafis rates (JCB and Discover Diners) will display in
  // the same table or separately.
  sameCafisRate: computed('cafisJcb', 'cafisDiscoverDiners', 'cafisJcbKeyed', 'cafisDiscoverDinersKeyed', 'cafisJcbInvoice', 'cafisDiscoverDinersInvoice', 'cafisDiscoverDinersInvoiceDashboard', 'cafisJcbInvoiceCardOnFile', 'cafisJcbInvoiceDashboard', 'cafisDiscoverDinersInvoiceCardOnFile', 'cafisJcbTerminal', 'cafisJcbTerminalOnFile', 'cafisDiscoverDinersTerminal', 'cafisDiscoverDinersTerminalOnFile', 'cafisJcbExternalApi', 'cafisDiscoverDinersExternalApi', 'cafisJcbEcomFree', 'cafisDiscoverDinersEcomFree', 'cafisJcbEcomPremium', 'cafisDiscoverDinersEcomPremium', function () {
    const registerEqual = this.cafisJcb.equals(this.cafisDiscoverDiners) && this.cafisJcbKeyed.equals(this.cafisDiscoverDinersKeyed);
    const invoicesEqual = !this.cafisJcbInvoice || this.cafisJcbInvoice.equals(this.cafisDiscoverDinersInvoice);
    const invoicesOnFileEqual = !this.cafisJcbInvoiceCardOnFile || this.cafisJcbInvoiceCardOnFile.equals(this.cafisDiscoverDinersInvoiceCardOnFile);
    const invoiceDashboardEqual = !this.cafisJcbInvoiceDashboard || this.cafisJcbInvoiceDashboard.equals(this.cafisDiscoverDinersInvoiceDashboard);
    const externalApiEqual = this.cafisJcbExternalApi.equals(this.cafisDiscoverDinersExternalApi);
    const ecomFreeEqual = this.cafisJcbEcomFree.equals(this.cafisDiscoverDinersEcomFree);
    const ecomPremiumEqual = this.cafisJcbEcomPremium.equals(this.cafisDiscoverDinersEcomPremium);
    const terminalEqual = this.cafisJcbTerminal.equals(this.cafisDiscoverDinersTerminal) && this.cafisJcbTerminalOnFile.equals(this.cafisDiscoverDinersTerminalOnFile);
    return registerEqual && invoicesEqual && invoicesOnFileEqual && invoiceDashboardEqual && externalApiEqual && ecomFreeEqual && ecomPremiumEqual && terminalEqual;
  }),
  // Check both CP and CNP to find a Cost+ fee rate.
  // TODO(kaneda): Fix this condition. We currently assume that Cost+ fee is applied
  // to every product/entry method.
  costPlusRate: condition('cardPresent.costPlusFeeRate', {
    then: 'cardPresent.costPlusFeeRate',
    else: 'cardNotPresent.costPlusFeeRate'
  }),
  // In Interchange+, fees are being applied to every product/entry method
  // For V2 headline rate merchants, check for INTERCHANGE_PLUS_MARGIN rate
  // For V1 legacy merchants, check the interchangePlusFeeRate on fragments
  interchangePlusRate: computed('hasHeadlineRates', 'cardPresent.interchangePlusFeeRate', 'cardNotPresent.interchangePlusFeeRate', function () {
    if (this.hasHeadlineRates) {
      return this.getRateByName(HeadlineFeeRateName.INTERCHANGE_PLUS_MARGIN);
    }
    return this.cardPresent?.interchangePlusFeeRate || this.cardNotPresent?.interchangePlusFeeRate;
  })
}).reopenClass({
  _modelNameForMigration: 'processing-fees'
});